import {
  AccountInformation,
  PendingEval,
  PendingMerchants,
  RecentActivityAdvanced,
} from '@features/admin/status';
import { Tab } from '@features/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  AdminOverviewStatus,
  TPendingMerchant,
  TRecentActivity,
  TTab,
} from '@src/types';
import {
  getOverViewStatus,
  getPendingEval,
  getRecentActivity,
} from '@src/services/status.ts';
import { getPendingMerchants } from '@src/services/pendingMerchant.ts';

const defaultPagination = {
  currentPage: 1,
  pageSize: 20,
  totalPages: 0,
  totalItems: 0,
};

export const StatusPage = () => {
  const [tabActive, setTabActive] = useState(0);
  const [pendingEvalData, setPendingEvalData] = useState<number>(0);
  const [overViewData, setOverViewData] = useState<AdminOverviewStatus | null>(
    null
  );

  const [recentActivityData, setRecentActivityData] = useState<
    TRecentActivity[]
  >([]);
  const [recentActivityPagination, setRecentActivityPagination] =
    useState(defaultPagination);

  const [pendingMerchantsData, setPendingMerchantsData] = useState<
    TPendingMerchant[]
  >([]);
  const [pendingMerchantsPagination, setPendingMerchantsPagination] =
    useState(defaultPagination);

  const fetchRecentActivity = async (page = 1, size = 20) => {
    try {
      const res = await getRecentActivity({ page, size });

      setRecentActivityData(res.data.data);
      setRecentActivityPagination({
        currentPage: res.data.page,
        pageSize: res.data.size,
        totalPages: res.data.total_pages,
        totalItems: res.data.total,
      });
    } catch (error) {
      setRecentActivityData([]);
      setRecentActivityPagination((prev) => ({ ...prev, totalItems: 0 }));
    }
  };

  const [pendingMerchantsCount, setPendingMerchantsCount] = useState<number>(0);

  const fetchPendingMerchants = async (page = 1, pageSize = 20) => {
    try {
      const res = await getPendingMerchants({ page, page_size: pageSize });

      setPendingMerchantsData(res.data.data);
      setPendingMerchantsPagination({
        currentPage: res.data.current_page,
        pageSize: res.data.page_size,
        totalPages: res.data.total_pages,
        totalItems: res.data.total,
      });
      setPendingMerchantsCount(res.data.total);
    } catch (error) {
      setPendingMerchantsData([]);
      setPendingMerchantsPagination((prev) => ({ ...prev, totalItems: 0 }));
      setPendingMerchantsCount(0);
    }
  };

  const fetchPendingEval = async () => {
    const res = await getPendingEval();

    setPendingEvalData(res.data);
  };

  const fetchOverViewData = async () => {
    const res = await getOverViewStatus();

    setOverViewData(res.data);
  };

  useEffect(() => {
    fetchPendingEval();
    fetchOverViewData();
    fetchRecentActivity();
    fetchPendingMerchants();
  }, []);

  const tabs: TTab[] = useMemo(
    () => [
      {
        label: 'Recent Activity',
        component: (
          <RecentActivityAdvanced
            data={recentActivityData}
            pagination={recentActivityPagination}
            onPageChange={(page) =>
              fetchRecentActivity(page, recentActivityPagination.pageSize)
            }
            onPageSizeChange={(size) => fetchRecentActivity(1, size)}
          />
        ),
      },
      // {
      //   label: 'Pending Performance Accounts',
      //   component: <PendingPerformanceAccounts />,
      //   count: 9,
      // },
      {
        label: 'Pending Eval > PA Accounts',
        component: <PendingEval />,
        count: pendingEvalData,
      },
      {
        label: 'Pending Merchants',
        component: (
          <PendingMerchants
            data={pendingMerchantsData}
            pagination={pendingMerchantsPagination}
            onPageChange={(page) =>
              fetchPendingMerchants(page, pendingMerchantsPagination.pageSize)
            }
            onPageSizeChange={(size) => fetchPendingMerchants(1, size)}
          />
        ),
        count: pendingMerchantsCount,
      },
    ],
    [
      recentActivityData,
      recentActivityPagination,
      pendingEvalData,
      pendingMerchantsData,
      pendingMerchantsPagination,
      pendingMerchantsCount,
    ]
  );

  return (
    <div className={'min-w-[900px]'}>
      <AccountInformation overViewData={overViewData} />
      <Tab
        className={'mt-3 mb-2'}
        tabActive={tabActive}
        tabs={tabs}
        onChangeTab={setTabActive}
      />
    </div>
  );
};
